import { useEffect, useMemo, useState } from "react";
import type {
  ArtikelMatch,
  KonsolidiertePosition,
  Schnellkalkulation,
} from "@bidi-handel/core/schemas";
import type { PendingMatchAction } from "../DetailPage";
import { api, ApiError } from "../../api/client";
import { useApi } from "../../hooks/useApi";
import { Icon } from "../../shell/Icon";
import {
  buildOzTree,
  countPicked,
  countPositions,
  type OzTreeNode,
  type PositionWithKonsol,
} from "./oz-tree";
import { ArticlePickerDialog } from "./ArticlePickerDialog";
import { MatchDrawer, topCandidate } from "./MatchDrawer";
import { KomponentenDialog } from "./KomponentenDialog";

export function MatchTab({
  tenantId,
  anfrageId,
  onKlaerpunkteMaybeChanged,
  pendingAction,
  onActionConsumed,
}: {
  tenantId: string;
  anfrageId: string;
  /** Wird nach Mutationen aufgerufen, die Klärpunkte resolven können
   *  (z.B. „Nicht anbieten"-Toggle resolved Backend-seitig alle Klärpunkte
   *  zur Position). Default: no-op. */
  onKlaerpunkteMaybeChanged?: () => void;
  /** Cross-Tab-Trigger aus dem Klaerpunkte-Tab: oeffnet beim Match-Tab-Switch
   *  direkt den passenden Drawer / Komponenten-Dialog fuer eine Position. */
  pendingAction?: PendingMatchAction | null;
  onActionConsumed?: () => void;
}) {
  const matchRes = useApi(() => api.getMatch(tenantId, anfrageId), [tenantId, anfrageId]);
  const konsolRes = useApi(
    () => api.getKonsolidiert(tenantId, anfrageId),
    [tenantId, anfrageId]
  );

  const [local, setLocal] = useState<ArtikelMatch | null>(null);
  const [pending, setPending] = useState<string | null>(null);
  const [pickError, setPickError] = useState<string | null>(null);
  const [pickerForPosition, setPickerForPosition] =
    useState<PositionWithKonsol | null>(null);
  const [komponentenForPosition, setKomponentenForPosition] =
    useState<PositionWithKonsol | null>(null);
  // drawerForPositionId statt full Object, damit nach Pick-Update aus
  // `local` immer die frischen Match-Daten im Drawer landen.
  const [drawerForPositionId, setDrawerForPositionId] = useState<string | null>(null);
  // Default: alle Gruppen offen. closedGroups trackt explizit zugeklappte
  // Pfade, damit Tests + initial-Rendering den vollen Tree sehen.
  const [closedGroups, setClosedGroups] = useState<Set<string>>(() => new Set());

  const matchData = local ?? (matchRes.state.status === "ok" ? matchRes.state.data : null);
  const konsolData =
    konsolRes.state.status === "ok" ? konsolRes.state.data : null;

  async function handlePick(positionId: string, artikelCode: string | null) {
    setPending(positionId);
    setPickError(null);
    try {
      const updated = await api.pickMatch(tenantId, anfrageId, positionId, artikelCode);
      setLocal(updated);
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { hint?: string; error?: string } | null;
        setPickError(body?.hint ?? body?.error ?? err.message);
      } else {
        setPickError(String(err));
      }
      throw err;
    } finally {
      setPending(null);
    }
  }

  async function handleSetNichtAngeboten(positionId: string, value: boolean) {
    setPending(positionId);
    setPickError(null);
    try {
      const updated = await api.setNichtAngeboten(
        tenantId,
        anfrageId,
        positionId,
        value
      );
      setLocal(updated);
      // Backend resolved Klärpunkte zur Position wenn nichtAngeboten=true →
      // Tab-Count und KlaerpunkteTab müssen neu laden.
      onKlaerpunkteMaybeChanged?.();
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { hint?: string; error?: string } | null;
        setPickError(body?.hint ?? body?.error ?? err.message);
      } else {
        setPickError(String(err));
      }
      throw err;
    } finally {
      setPending(null);
    }
  }

  async function handleSetSchnellkalkulation(
    positionId: string,
    schnellkalkulation: Schnellkalkulation | null
  ) {
    setPending(positionId);
    setPickError(null);
    try {
      const updated = await api.setSchnellkalkulation(
        tenantId,
        anfrageId,
        positionId,
        schnellkalkulation
      );
      setLocal(updated);
      onKlaerpunkteMaybeChanged?.();
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { hint?: string; error?: string } | null;
        setPickError(body?.hint ?? body?.error ?? err.message);
      } else {
        setPickError(String(err));
      }
      throw err;
    } finally {
      setPending(null);
    }
  }

  const konsolByPositionId = useMemo(() => {
    const m = new Map<string, KonsolidiertePosition>();
    if (konsolData) {
      for (const p of konsolData.positionen) m.set(p.positionId, p);
    }
    return m;
  }, [konsolData]);

  // Cross-Tab-Aktion verbrauchen: wenn aus dem Klaerpunkte-Tab eine
  // pendingAction reinkommt, die passende Position raussuchen und Drawer
  // bzw. Komponenten-Dialog oeffnen. Wartet implizit bis matchData geladen
  // ist (Effect re-runs nach jedem Daten-Update).
  useEffect(() => {
    if (!pendingAction || !matchData) return;
    const matchPos = matchData.positionen.find(
      (p) => p.positionId === pendingAction.positionId
    );
    if (!matchPos) {
      // Position nicht (mehr) im Match — Trigger sauber verwerfen, sonst
      // bleibt er stehen und wird bei jedem Re-Render wieder gefeuert.
      onActionConsumed?.();
      return;
    }
    if (pendingAction.action === "drawer") {
      setDrawerForPositionId(matchPos.positionId);
    } else if (pendingAction.action === "schnellkalk") {
      setKomponentenForPosition({
        match: matchPos,
        konsol: konsolByPositionId.get(matchPos.positionId) ?? null,
      });
    }
    onActionConsumed?.();
  }, [pendingAction, matchData, konsolByPositionId, onActionConsumed]);

  const tree = useMemo(() => {
    if (!matchData) return [];
    return buildOzTree(matchData.positionen, konsolByPositionId);
  }, [matchData, konsolByPositionId]);

  // Aktuelle Drawer-Position frisch aus matchData ableiten (sodass nach
  // Pick-Update der Drawer die neuen Daten zeigt).
  const drawerFor: PositionWithKonsol | null = useMemo(() => {
    if (!drawerForPositionId || !matchData) return null;
    const pos = matchData.positionen.find((p) => p.positionId === drawerForPositionId);
    if (!pos) return null;
    return { match: pos, konsol: konsolByPositionId.get(pos.positionId) ?? null };
  }, [drawerForPositionId, matchData, konsolByPositionId]);

  if (matchRes.state.status === "loading" && !local) {
    return (
      <div className="ueber">
        <section className="block">
          <div className="block-head">
            <span className="uppercase-mini">// MATCH LADEN …</span>
          </div>
        </section>
      </div>
    );
  }

  if (matchRes.state.status === "error" && !local) {
    return (
      <div className="ueber">
        <section className="block" style={{ borderColor: "oklch(0.72 0.18 25 / 0.4)" }}>
          <div className="block-head">
            <span className="uppercase-mini" style={{ color: "var(--bad)" }}>
              // MATCH NICHT LADBAR
            </span>
          </div>
          <div className="notice-body">
            <div className="mono" style={{ color: "var(--bad)" }}>
              {matchRes.state.error.message}
            </div>
            <div>
              <button type="button" className="btn" onClick={matchRes.refetch}>
                <Icon name="refresh" size={14} /> Nochmal versuchen
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (!matchData || matchData.positionen.length === 0) {
    return (
      <div className="ueber">
        <section className="block">
          <div className="block-head">
            <span className="uppercase-mini">// MATCH</span>
          </div>
          <div className="notice-body">
            <div className="dim">
              Phase 4 lieferte keine Positionen. Lief der Match-Agent überhaupt?
            </div>
          </div>
        </section>
      </div>
    );
  }

  const total = matchData.positionen.length;
  const withMatch = matchData.positionenMitMatch;
  const pct = total > 0 ? (withMatch / total) * 100 : 0;
  const offen = total - withMatch;

  return (
    <div className="ueber">
      <div className="match-stats">
        <div className="ms-pill">
          <span className="mono ms-num">{withMatch}</span>
          <span className="ms-of">/ {total}</span>
          <span className="uppercase-mini">Positionen mit Auswahl</span>
        </div>
        <div className="ms-progress">
          <div className="ms-progress-fill" style={{ width: `${pct}%` }} />
        </div>
        <div className="ms-rest">
          {offen > 0 && (
            <span className="pill warn">
              <span className="dot" />
              {offen} OFFEN
            </span>
          )}
          <span className="pill ok">
            <span className="dot" />
            {withMatch} GEMATCHT
          </span>
        </div>
      </div>

      {konsolRes.state.status === "error" && (
        <div
          className="warn-line"
          style={{ marginBottom: 14, borderLeftColor: "var(--warn)" }}
        >
          LV-Originaltext nicht verfügbar — Phase 3 fehlt.
        </div>
      )}

      {pickError && (
        <div
          className="warn-line"
          style={{
            marginBottom: 14,
            borderLeftColor: "var(--bad)",
            background: "oklch(0.72 0.18 25 / 0.08)",
          }}
        >
          <strong>Auswahl fehlgeschlagen:</strong>{" "}
          <span className="mono">{pickError}</span>
        </div>
      )}

      <div className="match-list">
        {tree.map((node) => (
          <GroupNode
            key={node.path || "_root"}
            node={node}
            pending={pending}
            closedGroups={closedGroups}
            onToggle={(p) =>
              setClosedGroups((cur) => {
                const next = new Set(cur);
                if (next.has(p)) next.delete(p);
                else next.add(p);
                return next;
              })
            }
            onOpenDrawer={(p) => setDrawerForPositionId(p.match.positionId)}
          />
        ))}
      </div>

      {drawerFor && (
        <MatchDrawer
          position={drawerFor}
          pending={pending}
          onPick={handlePick}
          onSetNichtAngeboten={handleSetNichtAngeboten}
          onSetSchnellkalkulation={handleSetSchnellkalkulation}
          onOpenPicker={(p) => {
            setPickerForPosition(p);
            setDrawerForPositionId(null);
          }}
          onOpenKomponenten={(p) => {
            setKomponentenForPosition(p);
            setDrawerForPositionId(null);
          }}
          onClose={() => setDrawerForPositionId(null)}
        />
      )}

      {pickerForPosition && (
        <ArticlePickerDialog
          tenantId={tenantId}
          positionId={pickerForPosition.match.positionId}
          positionOz={pickerForPosition.match.oz}
          originaltext={
            pickerForPosition.konsol?.originaltext ??
            "(LV-Originaltext nicht verfügbar)"
          }
          lvEinheit={pickerForPosition.konsol?.einheit ?? "?"}
          lvMenge={pickerForPosition.konsol?.menge ?? 0}
          currentArtikelCode={pickerForPosition.match.gewaehlterArtikel}
          onClose={() => setPickerForPosition(null)}
          onPicked={async (code) =>
            handlePick(pickerForPosition.match.positionId, code)
          }
        />
      )}

      {komponentenForPosition && (
        <KomponentenDialog
          tenantId={tenantId}
          positionId={komponentenForPosition.match.positionId}
          positionOz={komponentenForPosition.match.oz}
          originaltext={
            komponentenForPosition.konsol?.originaltext ??
            "(LV-Originaltext nicht verfügbar)"
          }
          lvEinheit={komponentenForPosition.konsol?.einheit ?? "?"}
          lvMenge={komponentenForPosition.konsol?.menge ?? 0}
          kurzText={komponentenForPosition.konsol?.kurzText}
          hauptgruppe={komponentenForPosition.konsol?.hauptgruppe}
          provisional={komponentenForPosition.konsol?.provisional}
          existing={komponentenForPosition.match.schnellkalkulation}
          onClose={() => setKomponentenForPosition(null)}
          onSaved={async (schnellkalkulation) => {
            await handleSetSchnellkalkulation(
              komponentenForPosition.match.positionId,
              schnellkalkulation
            );
          }}
        />
      )}
    </div>
  );
}

function GroupNode({
  node,
  pending,
  closedGroups,
  onToggle,
  onOpenDrawer,
}: {
  node: OzTreeNode;
  pending: string | null;
  closedGroups: Set<string>;
  onToggle: (path: string) => void;
  onOpenDrawer: (p: PositionWithKonsol) => void;
}) {
  const total = countPositions(node);
  const picked = countPicked(node);
  const hasHeader = node.path !== "";
  const isOpen = !hasHeader || !closedGroups.has(node.path);

  if (!hasHeader) {
    return (
      <>
        {node.positions.map((p) => (
          <MatchPosRow
            key={p.match.positionId}
            pos={p}
            pending={pending}
            onOpenDrawer={onOpenDrawer}
          />
        ))}
        {node.children.map((child) => (
          <GroupNode
            key={child.path}
            node={child}
            pending={pending}
            closedGroups={closedGroups}
            onToggle={onToggle}
            onOpenDrawer={onOpenDrawer}
          />
        ))}
      </>
    );
  }

  return (
    <div className={`match-group ${isOpen ? "open" : ""}`}>
      <button type="button" className="match-group-head" onClick={() => onToggle(node.path)}>
        <span className="mg-chev">
          <Icon name="chev" size={14} />
        </span>
        <span className="mg-code mono">{node.path}</span>
        <span className="mg-name">Gruppe</span>
        <span className="mg-count uppercase-mini">
          {total} Position{total === 1 ? "" : "en"}
        </span>
        <span className={`pill ${picked === total ? "ok" : picked > 0 ? "acc" : "warn"}`}>
          <span className="dot" />
          {picked}/{total} GEWÄHLT
        </span>
      </button>
      {isOpen && (
        <div
          className="match-group-body"
          style={{ display: "grid", gap: 0, padding: "8px 12px 12px" }}
        >
          {node.positions.map((p) => (
            <MatchPosRow
              key={p.match.positionId}
              pos={p}
              pending={pending}
              onOpenDrawer={onOpenDrawer}
            />
          ))}
          {node.children.map((child) => (
            <GroupNode
              key={child.path}
              node={child}
              pending={pending}
              closedGroups={closedGroups}
              onToggle={onToggle}
              onOpenDrawer={onOpenDrawer}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Kompakte 1-Zeilen-Position für die MatchTab-Liste. Zeigt OZ, Originaltext,
 * Top-Kandidat-Code + Score, Pick-Status-Pill, Chevron. Klick öffnet den
 * MatchDrawer mit der vollen Kandidaten-Liste.
 */
function MatchPosRow({
  pos,
  pending,
  onOpenDrawer,
}: {
  pos: PositionWithKonsol;
  pending: string | null;
  onOpenDrawer: (p: PositionWithKonsol) => void;
}) {
  const { match, konsol } = pos;
  const picked = match.gewaehlterArtikel;
  const nichtAngeboten = match.nichtAngeboten;
  const schnellkalk = match.schnellkalkulation;
  const schnellkalkScore = schnellkalk ? (schnellkalk.closed ? 100 : 75) : null;
  const top = topCandidate(match);
  const isPending = pending === match.positionId;
  const displayScore = schnellkalkScore ?? top?.matchScore ?? null;
  const scoreColor =
    nichtAngeboten || displayScore == null
      ? "var(--fg-faint)"
      : displayScore >= 80
        ? "var(--ok)"
        : displayScore >= 40
          ? "var(--warn)"
          : "var(--bad)";

  return (
    <button
      type="button"
      className={`match-pos-row ${nichtAngeboten ? "is-excluded" : ""}`}
      onClick={() => onOpenDrawer(pos)}
      disabled={isPending}
    >
      <span className="mpr-oz">{match.oz}</span>
      <span className="mpr-text">
        {konsol?.metadata?.wandKonstruktion && (
          <span
            className="mpr-konstr-tag"
            title={`Konstruktions-Position (gezeichnete Wand): ${konsol.metadata.wandKonstruktion.bauweise} ${konsol.metadata.wandKonstruktion.code}`}
          >
            Konstr · {konsol.metadata.wandKonstruktion.code}
          </span>
        )}
        <span className="mpr-text-label">
          {konsol?.originaltext ?? "(LV-Originaltext nicht verfügbar)"}
        </span>
      </span>
      <span className="mpr-top-cand mono">
        {schnellkalk ? (
          <span style={{ color: "var(--ok)", fontWeight: 600 }}>
            Schnellkalkulation
          </span>
        ) : top ? (
          top.artikelCode
        ) : (
          <span className="dim">—</span>
        )}
      </span>
      <span className="mpr-score">
        <svg viewBox="0 0 28 28" width="28" height="28">
          <circle cx="14" cy="14" r="11" stroke="var(--line)" strokeWidth="2.5" fill="none" />
          {displayScore != null && !nichtAngeboten && (
            <circle
              cx="14"
              cy="14"
              r="11"
              stroke={scoreColor}
              strokeWidth="2.5"
              fill="none"
              strokeDasharray={`${(displayScore / 100) * 69.1} 69.1`}
              transform="rotate(-90 14 14)"
              strokeLinecap="round"
            />
          )}
        </svg>
        <span
          className="mono"
          style={{
            fontSize: 11,
            color: nichtAngeboten ? "var(--fg-faint)" : "var(--fg)",
          }}
        >
          {displayScore != null && !nichtAngeboten ? displayScore : "—"}
        </span>
      </span>
      <span className="mpr-status">
        {nichtAngeboten ? (
          <span className="pill excluded" style={{ fontSize: 10 }}>
            <span className="dot" />
            Nicht angeboten
          </span>
        ) : schnellkalk ? (
          <span
            className={`pill ${schnellkalk.closed ? "ok" : "warn"}`}
            style={{ fontSize: 10 }}
          >
            <span className="dot" />
            {schnellkalk.closed
              ? "Schnellkalk: abgeschlossen"
              : "Schnellkalk: in Arbeit"}
          </span>
        ) : (
          <span className={`pill ${picked ? "ok" : "warn"}`} style={{ fontSize: 10 }}>
            <span className="dot" />
            {picked ? `Gewählt: ${picked}` : "Kein Pick"}
          </span>
        )}
      </span>
      <span className="mpr-chev">
        <Icon name="chev" size={14} />
      </span>
    </button>
  );
}
